import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import type { DataFreshnessCardData, DataFreshnessEntry } from "store/types";

interface Props {
  data?: DataFreshnessCardData;
  loading?: boolean;
}

function daysAgo(dateStr: string): number {
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function freshnessColor(days: number): string {
  if (days <= 7) return "#0F5D5E";
  if (days <= 30) return "#D97706";
  return "#C53030";
}

function freshnessLabel(days: number): string {
  if (days <= 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

export const DataFreshnessCard: React.FC<Props> = ({ data, loading }) => {
  if (loading || !data) {
    return (
      <Box sx={{ p: "16px 8px 16px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  const entries: DataFreshnessEntry[] = data.dataFreshness ?? [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#6B7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 1.5
        }}
      >
        Data Freshness
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {entries.map((entry) => {
          const days = daysAgo(entry.asOfDate);
          const color = freshnessColor(days);
          return (
            <Box
              key={entry.label}
              sx={{ display: "flex", alignItems: "center", gap: 1, p: "6px 10px", bgcolor: "#F9FAFB", borderRadius: 2 }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#243B53" }}>{entry.label}</Typography>
                <Typography sx={{ fontSize: 10, color: "#9CA3AF" }}>{entry.source}</Typography>
              </Box>
              <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color }}>{freshnessLabel(days)}</Typography>
                <Typography sx={{ fontSize: 10, color: "#BCCCDC" }}>{entry.asOfDate}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
